import React, { useState, useEffect } from 'react';
import { 
  Search, Download, User, Mail, Phone, MapPin, Calendar, 
  Globe, Heart, Users, GraduationCap, Plane, Home, Briefcase, 
  DollarSign, Shield, FileText, ChevronDown, ChevronUp, 
  CheckCircle, X, Eye, EyeOff, Sparkles, ExternalLink,
  Filter, SortAsc, SortDesc, RefreshCw, AlertCircle,
  Clipboard, ClipboardCheck, Clock, Star, Award, Crown,
  BookOpen, Building, CreditCard, HeartPulse, Scale,
  Info, Plus, Minus, File, Image, FileText as FileTextIcon,
  FileSpreadsheet, Printer, FileText as FileIcon
} from 'lucide-react';
// import './ApplicationLookup.css';

function ApplicationLookup() {
  const [email, setEmail] = useState('');
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [downloading, setDownloading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Update handleSearch to accept emailToSearch
  const handleSearch = async (e, emailToSearch) => {
    if (e) e.preventDefault();

    const searchEmail = emailToSearch || email;
    if (!searchEmail) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `https://api.truvz.com/api/visa-applications/email/${encodeURIComponent(searchEmail)}`
      );
      const data = await response.json();

      if (response.ok) {
        const apps = data.data || data;
        setApplications(apps);

        if (!searchHistory.includes(searchEmail.toLowerCase())) {
          setSearchHistory(prev => [searchEmail.toLowerCase(), ...prev.slice(0, 4)]);
        }
      } else {
        setError(data.message || 'No applications found');
      }
    } catch (error) {
      setError('Error fetching applications. Please try again.');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 👇 Auto-run if URL has ?email=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryEmail = params.get('email');

    console.log("queryEmail",queryEmail);
    console.log("params",params);
    if (queryEmail) {
      setEmail(queryEmail);
      handleSearch(null, queryEmail);
    }
  }, []);

  const downloadAsExcel = async (application) => {
    setDownloading(true);
    try {
      // Create a hidden iframe to handle the download
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `https://api.truvz.com/api/visa-applications/${application.id}/export-excel`;
      document.body.appendChild(iframe);
      
      // Remove the iframe after a reasonable time
      setTimeout(() => {
        document.body.removeChild(iframe);
        setDownloading(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Error downloading Excel file');
      setDownloading(false);
    }
  };

  const downloadAllAsExcel = async () => {
    if (applications.length === 0) return;
    
    setDownloading(true);
    try {
      const response = await fetch(`https://api.truvz.com/api/visa-applications/export-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ applications }),
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `visa-applications-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Error downloading Excel file');
    } finally {
      setDownloading(false);
    }
  };

  // New function to generate and download PDF
  const downloadAsPdf = async (application) => {
    setGeneratingPdf(true);
    try {
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${application.id}/export-pdf`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `visa-application-${application.id}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Remove the old JSON download function
  // const downloadApplication = (application) => {
  //   const dataStr = JSON.stringify(application, null, 2);
  //   const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
  //   
  //   const exportFileDefaultName = `visa-application-${application.email}-${new Date().toISOString().split('T')[0]}.json`;
  //   
  //   const linkElement = document.createElement('a');
  //   linkElement.setAttribute('href', dataUri);
  //   linkElement.setAttribute('download', exportFileDefaultName);
  //   linkElement.click();
  // };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6B7280', icon: FileText, label: 'Draft' },
      submitted: { color: '#3B82F6', icon: Clock, label: 'Submitted' },
      under_review: { color: '#715ab7', icon: RefreshCw, label: 'Under Review' },
      approved: { color: '#10B981', icon: CheckCircle, label: 'Approved' },
      rejected: { color: '#EF4444', icon: X, label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <span className="status-badge" style={{ backgroundColor: config.color + '20', color: config.color }}>
        <Icon size={16} />
        {config.label}
      </span>
    );
  };

  const sortedApplications = [...applications].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      case 'oldest':
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      case 'name':
        return (a.full_name || '').localeCompare(b.full_name || '');
      default:
        return 0;
    }
  });

  const DetailSection = ({ icon: Icon, title, isExpanded, onToggle, children }) => (
    <div className="detail-section">
      <div className="section-header" onClick={onToggle}>
        <div className="section-title">
          <Icon size={20} />
          <h3>{title}</h3>
        </div>
        <div className="section-toggle">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      {isExpanded && (
        <div className="section-content">
          {children}
        </div>
      )}
    </div>
  );

  const DetailItem = ({ icon: Icon, label, value }) => (
    <div className="detail-item">
      <div className="detail-label">
        {Icon && <Icon size={16} />}
        <span>{label}</span>
      </div>
      <div className="detail-value">{value || 'Not provided'}</div>
    </div>
  );

  const ApplicationDetailView = ({ application }) => {
    if (!application) return null;

    return (
      <div className="application-detail">
        <div className="detail-header">
          <div className="header-content">
            <h2>Application Details</h2>
            <p className="application-id">ID: {application.id || 'N/A'}</p>
            <p className="application-date">Submitted: {formatDateTime(application.createdAt)}</p>
          </div>
          <div className="header-actions">
            {getStatusBadge(application.status)}
            <button 
              className="download-btn"
              onClick={() => downloadAsExcel(application)}
              disabled={downloading}
            >
              {downloading ? <RefreshCw size={16} className="spin" /> : <FileSpreadsheet size={16} />}
              {downloading ? 'Generating...' : 'Export Excel'}
            </button>
            <button 
              className="download-btn"
              onClick={() => downloadAsPdf(application)}
              disabled={generatingPdf}
            >
              {generatingPdf ? <RefreshCw size={16} className="spin" /> : <FileIcon size={16} />}
              {generatingPdf ? 'Generating...' : 'Export PDF'}
            </button>
          </div>
        </div>

        {/* Personal Information */}
        <DetailSection 
          icon={User}
          title="Personal Information"
          isExpanded={expandedSections.personal}
          onToggle={() => toggleSection('personal')}
        >
          <div className="detail-grid">
            <DetailItem icon={User} label="Full Name" value={application.full_name} />
            <DetailItem icon={Calendar} label="Date of Birth" value={formatDate(application.date_of_birth)} />
            <DetailItem icon={Globe} label="Nationality" value={application.nationality} />
            <DetailItem icon={Mail} label="Email" value={application.email} />
            <DetailItem icon={Phone} label="Contact Number" value={application.contact_number} />
            <DetailItem icon={MapPin} label="Address" value={application.address} />
            <DetailItem icon={Heart} label="Marital Status" value={application.marital_status} />
          </div>

          {application.spouse_name && (
            <>
              <div className="sub-section-header">
                <Users size={18} />
                <h4>Spouse Information</h4>
              </div>
              <div className="detail-grid">
                <DetailItem icon={User} label="Spouse Name" value={application.spouse_name} />
                <DetailItem icon={Calendar} label="Spouse DOB" value={formatDate(application.spouse_dob)} />
                <DetailItem icon={Globe} label="Spouse Nationality" value={application.spouse_nationality} />
              </div>
            </>
          )}

          {application.children_details && application.children_details.length > 0 && (
            <>
              <div className="sub-section-header">
                <Users size={18} />
                <h4>Children Information</h4>
              </div>
              {application.children_details.map((child, index) => (
                <div key={index} className="child-detail card">
                  <h5>Child {index + 1}</h5>
                  <div className="detail-grid">
                    <DetailItem icon={User} label="Name" value={child.name} />
                    <DetailItem icon={Calendar} label="Date of Birth" value={formatDate(child.dob)} />
                    <DetailItem icon={Globe} label="Nationality" value={child.nationality} />
                    <DetailItem label="Gender" value={child.gender} />
                  </div>
                </div>
              ))}
            </>
          )}
        </DetailSection>

        {/* Education History */}
        <DetailSection 
          icon={GraduationCap}
          title="Education History"
          isExpanded={expandedSections.education}
          onToggle={() => toggleSection('education')}
        >
          {application.user_education_forms && application.user_education_forms.length > 0 ? (
            <>
              <div className="sub-section-header">
                <User size={18} />
                <h4>Your Education</h4>
              </div>
              {application.user_education_forms.map((edu, index) => (
                <div key={index} className="education-item card">
                  <h4>{edu.degree}</h4>
                  <div className="detail-grid">
                    <DetailItem label="Field of Study" value={edu.fieldOfStudy} />
                    <DetailItem label="Institution" value={edu.institution} />
                    <DetailItem label="Completion Year" value={edu.completionYear} />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="empty-state">
              <GraduationCap size={32} />
              <p>No education information provided</p>
            </div>
          )}

          {application.spouse_education_forms && application.spouse_education_forms.length > 0 && (
            <>
              <div className="sub-section-header">
                <Users size={18} />
                <h4>Spouse's Education</h4>
              </div>
              {application.spouse_education_forms.map((edu, index) => (
                <div key={index} className="education-item card">
                  <h4>{edu.degree}</h4>
                  <div className="detail-grid">
                    <DetailItem label="Field of Study" value={edu.fieldOfStudy} />
                    <DetailItem label="Institution" value={edu.institution} />
                    <DetailItem label="Completion Year" value={edu.completionYear} />
                  </div>
                </div>
              ))}
            </>
          )}
        </DetailSection>

        {/* Travel History */}
        <DetailSection 
          icon={Plane}
          title="Travel History"
          isExpanded={expandedSections.travel}
          onToggle={() => toggleSection('travel')}
        >
          {application.travel_info?.countries_traveled?.length > 0 ? (
            application.travel_info.countries_traveled.map((country, index) => (
              <div key={index} className="travel-item card">
                <div className="country-header">
                  <Globe size={20} />
                  <h4>{country}</h4>
                </div>
                <div className="detail-grid">
                  <DetailItem label="Date Visited" value={formatDate(application.country_details?.[country]?.date_visited)} />
                  <DetailItem label="Date Return" value={formatDate(application.country_details?.[country]?.date_return)} />
                  <DetailItem label="Duration" value={application.country_details?.[country]?.duration ? `${application.country_details[country].duration} days` : 'N/A'} />
                  <DetailItem label="Purpose" value={application.country_details?.[country]?.purpose} />
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <Plane size={32} />
              <p>No travel history provided</p>
            </div>
          )}
        </DetailSection>

        {/* Living History */}
        <DetailSection 
          icon={Home}
          title="Living History (Past 10 Years)"
          isExpanded={expandedSections.living}
          onToggle={() => toggleSection('living')}
        >
          {application.living_history && application.living_history.length > 0 ? (
            application.living_history.map((item, index) => (
              <div key={index} className="living-item card">
                <div className="country-header">
                  <MapPin size={20} />
                  <h4>{item.country}</h4>
                </div>
                <div className="detail-grid">
                  <DetailItem label="Start Date" value={formatDate(item.start_date)} />
                  <DetailItem label="End Date" value={item.is_current ? 'Current' : formatDate(item.end_date)} />
                  <DetailItem label="Current Residence" value={item.is_current ? 'Yes' : 'No'} />
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <Home size={32} />
              <p>No living history provided</p>
            </div>
          )}
        </DetailSection>

        {/* Employment History */}
        <DetailSection 
          icon={Briefcase}
          title="Employment History"
          isExpanded={expandedSections.employment}
          onToggle={() => toggleSection('employment')}
        >
          <DetailItem label="Employment Status" value={application.employment_status} />
          
          {application.employment_records && application.employment_records.length > 0 && (
            application.employment_records.map((job, index) => (
              <div key={index} className="employment-item card">
                <h4>Job {index + 1}</h4>
                <div className="detail-grid">
                  <DetailItem label="Employer Details" value={job.employer_details} />
                  <DetailItem label="Job Title" value={job.job_title} />
                  <DetailItem label="Salary" value={job.salary} />
                  <DetailItem label="Start Date" value={formatDate(job.job_start_date)} />
                  <DetailItem label="End Date" value={job.is_current_job ? 'Current' : formatDate(job.job_end_date)} />
                  <DetailItem label="Country" value={job.country} />
                  <DetailItem label="Current Job" value={job.is_current_job ? 'Yes' : 'No'} />
                </div>
              </div>
            ))
          )}
        </DetailSection>

        {/* Financial Information */}
        <DetailSection 
          icon={DollarSign}
          title="Financial Information"
          isExpanded={expandedSections.finance}
          onToggle={() => toggleSection('finance')}
        >
          <div className="detail-grid">
            <DetailItem icon={CreditCard} label="Bank Balance" value={formatCurrency(application.bank_balance)} />
            <DetailItem icon={DollarSign} label="Other Liquid Assets" value={application.financial_status} />
            <DetailItem icon={DollarSign} label="Annual Income" value={formatCurrency(application.annual_income)} />
            <DetailItem label="Source of Funds" value={application.fund_source} />
          </div>
        </DetailSection>

        {/* Ties to Home Country */}
        <DetailSection 
          icon={Home}
          title="Ties to Home Country"
          isExpanded={expandedSections.ties}
          onToggle={() => toggleSection('ties')}
        >
          <div className="detail-grid">
            <DetailItem label="Property Ownership/Assets" value={application.property_ownership} />
            <DetailItem label="Long-term Commitments" value={application.long_term_commitments} />
          </div>
        </DetailSection>

        {/* Health and Security */}
        <DetailSection 
          icon={Shield}
          title="Health and Security"
          isExpanded={expandedSections.health}
          onToggle={() => toggleSection('health')}
        >
          <div className="detail-grid">
            <DetailItem icon={HeartPulse} label="Health Issues" value={application.has_health_issues} />
            {application.has_health_issues === 'yes' && (
              <>
                <DetailItem label="Health Condition" value={application.disease_name === 'Other' ? application.custom_disease : application.disease_name} />
              </>
            )}
            
            <DetailItem icon={Scale} label="Criminal Record" value={application.has_criminal_record} />
            {application.has_criminal_record === 'yes' && (
              <>
                <DetailItem label="Type of Offense" value={application.case_name === 'Other' ? application.custom_case : application.case_name} />
              </>
            )}
          </div>
        </DetailSection>

        {/* Additional Information */}
        <DetailSection 
          icon={Info}
          title="Additional Information"
          isExpanded={expandedSections.additional}
          onToggle={() => toggleSection('additional')}
        >
          <div className="sub-section-header">
            <User size={18} />
            <h4>Family Information</h4>
          </div>
          <div className="detail-grid">
            <DetailItem label="Mother's Name" value={application.mothers_name} />
            <DetailItem label="Mother's Phone" value={application.mothers_phone} />
            <DetailItem label="Father's Name" value={application.fathers_name} />
            <DetailItem label="Father's Phone" value={application.fathers_phone} />
            <DetailItem label="Parents' Address" value={application.parents_address} />
          </div>

          <div className="sub-section-header">
            <Users size={18} />
            <h4>Canadian Contacts</h4>
          </div>
          <DetailItem label="Has Canadian Contacts" value={application.has_canadian_contacts} />
          
          {application.contacts && application.contacts.length > 0 && (
            application.contacts.map((contact, index) => (
              <div key={index} className="contact-item card">
                <h5>Contact {index + 1}</h5>
                <div className="detail-grid">
                  <DetailItem label="Name" value={contact.name} />
                  <DetailItem label="Phone Number" value={contact.number} />
                </div>
              </div>
            ))
          )}

          <div className="sub-section-header">
            <Home size={18} />
            <h4>Accommodation in Canada</h4>
          </div>
          <DetailItem label="Has Accommodation" value={application.has_accommodation} />
          {application.has_accommodation === 'yes' && (
            <DetailItem label="Accommodation Address" value={application.accommodation_address} />
          )}

          <div className="sub-section-header">
            <FileTextIcon size={18} />
            <h4>Supporting Documents</h4>
          </div>
          {application.attachments && application.attachments.length > 0 ? (
            <div className="attachments-grid">
              {application.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <File size={16} />
                  <span>{attachment.original_name}</span>
                  <span className="file-size">({Math.round(attachment.size / 1024)} KB)</span>
                </div>
              ))}
            </div>
          ) : (
            <p>No documents uploaded</p>
          )}

          <div className="sub-section-header">
            <FileTextIcon size={18} />
            <h4>Additional Comments</h4>
          </div>
          <div className="comments-section">
            <p>{application.comments || 'No additional comments provided'}</p>
          </div>
        </DetailSection>
      </div>
    );
  };

  return (
    <div className="application-lookup-container">
      {/* Animated Background */}
      <div className="animated-background">
        <div className="floating-shapes">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="floating-shape" style={{
              animationDelay: `${i * 0.5}s`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${15 + Math.random() * 10}s`
            }} />
          ))}
        </div>
      </div>

      <div className="lookup-content">
        {/* Header */}
        <div className="lookup-header">
          <div className="header-glow"></div>
          <div className="header-content">
            <div className="title-section">
              <Crown size={48} className="header-icon" />
              <div>
                <h1>Visa Application Portal</h1>
                <p>Retrieve and manage visa applications with ease</p>
              </div>
            </div>
            <div className="header-stats">
              <div className="stat">
                <Users size={20} />
                <span>{applications.length} Applications</span>
              </div>
              {/* {applications.length > 0 && (
                <button 
                  className="excel-download-all"
                  onClick={downloadAllAsExcel}
                  disabled={downloading}
                >
                  {downloading ? <RefreshCw size={16} className="spin" /> : <FileSpreadsheet size={16} />}
                  Download All as Excel
                </button>
              )} */}
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="search-section card">
          <div className="search-header">
            <h2>Find Applications</h2>
            <Sparkles size={24} className="sparkle-icon" />
          </div>
          
          <form onSubmit={(e) => handleSearch(e, email)} className="search-form">
            <div className="search-input-group">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                placeholder="Enter applicant's email address..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="search-input"
              />
              <button 
                type="submit" 
                disabled={loading}
                className="search-button"
              >
                {loading ? (
                  <RefreshCw size={16} className="spin" />
                ) : (
                  <Search size={16} />
                )}
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>

          {searchHistory.length > 0 && (
            <div className="search-history">
              <p>Recent searches:</p>
              <div className="history-chips">
                {searchHistory.map((historyEmail, index) => (
                  <button
                    key={index}
                    onClick={() => setEmail(historyEmail)}
                    className="history-chip"
                  >
                    {historyEmail}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message card">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError('')} className="close-error">
              <X size={16} />
            </button>
          </div>
        )}

        {applications.length > 0 && (
          <div className="applications-section">
            <div className="section-header">
              <h2>Application Results</h2>
              <div className="sort-controls">
                <Filter size={16} />
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name">By Name</option>
                </select>
              </div>
            </div>

            <div className="applications-grid">
              {sortedApplications.map((app, index) => (
                <div key={index} className="application-card card">
                  <div className="card-header">
                    <div className="app-avatar">
                      <User size={24} />
                    </div>
                    <div className="app-info">
                      <h3>{app.full_name || 'Unnamed Application'}</h3>
                      <div className="app-meta">
                        <span className="app-email" onClick={() => copyToClipboard(app.email)}>
                          <Mail size={12} />
                          {app.email}
                          {copiedEmail && <ClipboardCheck size={12} className="copy-success" />}
                        </span>
                        <span className="app-date">
                          <Clock size={12} />
                          {app.createdAt ? formatDateTime(app.createdAt) : 'Unknown date'}
                        </span>
                      </div>
                    </div>
                    {getStatusBadge(app.status)}
                  </div>

                  <div className="card-actions">
                    <button 
                      onClick={() => setSelectedApplication(selectedApplication?.email === app.email ? null : app)}
                      className="details-button"
                    >
                      {selectedApplication?.email === app.email ? (
                        <><EyeOff size={16} /> Hide Details</>
                      ) : (
                        <><Eye size={16} /> View Details</>
                      )}
                    </button>
                    <button 
                      onClick={() => downloadAsExcel(app)}
                      className="download-button"
                      disabled={downloading}
                    >
                      {downloading ? <RefreshCw size={16} className="spin" /> : <FileSpreadsheet size={16} />}
                    </button>
                    <button 
                      onClick={() => downloadAsPdf(app)}
                      className="download-button"
                      disabled={generatingPdf}
                    >
                      {generatingPdf ? <RefreshCw size={16} className="spin" /> : <FileIcon size={16} />}
                    </button>
                  </div>

                  {selectedApplication?.email === app.email && (
                    <ApplicationDetailView application={app} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {applications.length === 0 && !loading && !error && (
          <div className="empty-state-section card">
            <div className="empty-content">
              <Award size={64} className="empty-icon" />
              <h3>Ready to Search</h3>
              <p>Enter an email address above to retrieve visa applications</p>
              <div className="features-grid">
                <div className="feature">
                  <Search size={20} />
                  <span>Quick Search</span>
                </div>
                <div className="feature">
                  <Download size={20} />
                  <span>Export Data</span>
                </div>
                <div className="feature">
                  <Shield size={20} />
                  <span>Secure Access</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApplicationLookup;